import { DataResult, State as DataState } from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showSuccessToast } from '../../../../../common/global/helpers';
import Button from '../../../../../common/widgets/Button';
import { DataLoader } from '../../../../../common/widgets/DataLoader';
import Grid, { getSkip, GridCellTd } from '../../../../../common/widgets/Grid';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { UserAvatar } from '../../../components/UserAvatar';
import { config } from '../../../config';
import { accountStatusColor } from '../../../constants';
import { AccountInfoContext } from '../../../contexts/AccountInfoContext';
import { MerchantAccountRoles, MerchantAccountStatus } from '../../../enums';
import { MerchantAccountListModel } from '../../../models/accounts';
import { resources } from '../../../resources/resources';
import { merchantAccountsService } from '../../../services/merchant-accounts';
import {
  clearAccountInvitationSentInfoMessage,
  getAccountInvitationSentData,
} from '../Common/utils';
import { EditAccountDialog, EditUserForm } from './EditAccountDialog';
import { InactivateAccountDialog } from './InactivateAccountDialog';
import { SendInvitationDialog } from './SendInvitationDialog';

const initialDataState: DataState = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'fullName', dir: 'asc' }],
};

export const AccountsList = () => {
  const { t: translate } = useTranslation();
  const { accountInfo } = useContext(AccountInfoContext);
  const [accounts, setAccounts] = useState<MerchantAccountListModel[] | null>(
    null,
  );
  const [total, setTotal] = useState<number>(0);
  const [searchParams] = useSearchParams();
  const odataString = searchParams.get('dataState');
  const [dataState, setDataState] = useState<DataState>(
    odataString ? JSON.parse(decodeURI(odataString)) : initialDataState,
  );
  const [showErrorPanel, setShowErrorPanel] = useState<boolean | undefined>();
  const [showInviteDialog, setShowInviteDialog] = useState<boolean>(false);
  const [editAccountId, setEditAccountId] = useState<number | undefined>();
  const [deleteAccountId, setDeleteAccountId] = useState<number | undefined>();
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const navigate = useNavigate();

  const isLoading = _.isNil(accounts);
  const isAdmin = accountInfo.roleId === MerchantAccountRoles.Admin;

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    navigate(
      `/accounts?dataState=${encodeURI(
        JSON.stringify({
          ...e.dataState,
          skip: getSkip(dataState, e.dataState),
        }),
      )}`,
    );
  };

  const onUserEdited = (accountId: number, form: EditUserForm) => {
    const account = accounts?.find(e => e.id === accountId);
    if (!account) return;

    account.fullName = `${form.firstname} ${form.lastname}`;
    account.initials = `${form.firstname!.substring(
      0,
      1,
    )} ${form.lastname!.substring(0, 1)}`;
    account.roleName = form.role!.text;
  };

  const onUserInactivated = () => {
    showSuccessToast(translate(resources.accounts.dialog.delete.confirmation));
    setDeleteAccountId(undefined);
    setRefreshData(true);
  };

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);
    }
  }, [refreshData]);

  useEffect(() => {
    setDataState(
      odataString ? JSON.parse(decodeURI(odataString)) : initialDataState,
    );
  }, [odataString]);

  const onDataReceived = (data: DataResult, showErrorPanel?: boolean) => {
    setAccounts(data.data);
    setTotal(data.total);
    setShowErrorPanel(showErrorPanel);
  };

  const invitationSentData = getAccountInvitationSentData(
    accountInfo.manufacturerId,
  );

  const getAddUserButton = () => {
    if (isLoading || !isAdmin) return null;
    if (total < config.usersMaxNo) {
      return (
        <div className="m-page-action">
          <Button
            className="btn btn-solid"
            onClick={() => setShowInviteDialog(true)}
            icon={<Icons.MAddUser />}
          >
            <span className="btn-text">
              {translate(resources.accounts.list.addUser)}
            </span>
          </Button>
        </div>
      );
    }
    return (
      <div className="m-page-action">
        <PopoverDialog
          className="m-tooltip"
          content={
            <div>
              {translate(resources.accounts.list.addUserDisabledMessage)}
            </div>
          }
          type="hover"
          placement="bottom"
        >
          <span>
            <Button
              className="btn btn-solid"
              disabled
              icon={<Icons.MAddUser />}
            >
              <span className="btn-text">
                {translate(resources.accounts.list.addUser)}
              </span>
            </Button>
          </span>
        </PopoverDialog>
      </div>
    );
  };

  return (
    <>
      <div className="m-page-actions">
        <h1>
          {translate(resources.accounts.list.title, {
            0: !_.isNil(accounts)
              ? `(${total} ${translate(resources.of)} ${config.usersMaxNo})`
              : '',
          })}
        </h1>
        {getAddUserButton()}
      </div>
      {!!invitationSentData && (
        <Notification
          type={NotificationType.Info}
          showClose
          onCloseClick={() =>
            clearAccountInvitationSentInfoMessage(accountInfo.manufacturerId)
          }
          messages={[
            {
              message: translate(
                invitationSentData.resourceKey,
                invitationSentData.payload,
              ) as string,
            },
          ]}
        />
      )}
      <div className="m-panel">
        <DataLoader
          getData={merchantAccountsService.getAccounts}
          dataState={dataState}
          onDataReceived={onDataReceived}
          bypassRequestDataIfNeeded={refreshData}
        />
        <Loader
          loading={isLoading}
          loader={<LoadingSpinner />}
          showErrorPanel={showErrorPanel}
        >
          {!!accounts && (
            <div
              className={`m-grid m-members-grid ${
                isAdmin ? 'm-members-admin' : ''
              }`}
            >
              <Grid
                data={accounts}
                pageable={false}
                {...dataState}
                onDataStateChange={dataStateChange}
                total={total}
                sortable={{ allowUnsort: true, mode: 'multiple' }}
                columns={[
                  {
                    field: 'fullName',
                    title: translate(resources.accounts.list.columns.fullName),
                    cell: ({ dataItem }: GridCellProps) => {
                      return (
                        <GridCellTd>
                          <span className="m-user-table-name">
                            <UserAvatar
                              color1={dataItem.avatarColor1}
                              color2={dataItem.avatarColor2}
                              fontColor={dataItem.avatarFontColor}
                              initials={dataItem.initials}
                            />
                            <strong>{dataItem.fullName}</strong>
                          </span>
                        </GridCellTd>
                      );
                    },
                  },
                  {
                    field: 'roleName',
                    title: translate(resources.accounts.list.columns.role),
                    cell: ({ dataItem }: GridCellProps) => {
                      return (
                        <GridCellTd className="grid-center">
                          <span
                            style={{
                              backgroundColor: '#eaeaea',
                            }}
                            className="m-grid-badge"
                          >
                            {dataItem.roleName}
                          </span>
                        </GridCellTd>
                      );
                    },
                  },
                  {
                    field: 'email',
                    title: translate(resources.accounts.list.columns.email),
                    cell: ({ dataItem }: GridCellProps) => {
                      return <GridCellTd>{dataItem.email}</GridCellTd>;
                    },
                  },
                  {
                    field: 'statusName',
                    title: translate(resources.accounts.list.columns.status),
                    cell: ({ dataItem }: GridCellProps) => {
                      return (
                        <GridCellTd className="grid-center">
                          <span
                            style={{
                              backgroundColor:
                                accountStatusColor[
                                  dataItem.statusId as MerchantAccountStatus
                                ],
                            }}
                            className="m-grid-badge"
                          >
                            {dataItem.statusName}
                          </span>
                        </GridCellTd>
                      );
                    },
                  },
                  ...(isAdmin
                    ? [
                        {
                          title: translate(resources.actions),
                          cell: ({ dataItem }: GridCellProps) => {
                            if (accountInfo.id === dataItem.id)
                              return (
                                <GridCellTd className="grid-center"></GridCellTd>
                              );
                            return (
                              <GridCellTd className="grid-center">
                                <PopoverDialog
                                  className="popover-actions-list"
                                  type="click"
                                  placement="bottom"
                                  closeFromContent
                                  appendTo={document.body}
                                  content={
                                    <ActionsDropdown
                                      onEditClick={() =>
                                        setEditAccountId(dataItem.id)
                                      }
                                      onDeleteClick={() =>
                                        setDeleteAccountId(dataItem.id)
                                      }
                                    />
                                  }
                                >
                                  <a className="m-grid-action"></a>
                                </PopoverDialog>
                              </GridCellTd>
                            );
                          },
                          headerClassName: 'grid-center',
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </Loader>
      </div>
      {showInviteDialog && (
        <SendInvitationDialog closeDialog={() => setShowInviteDialog(false)} />
      )}
      {editAccountId && (
        <EditAccountDialog
          accountId={editAccountId}
          onUserEdited={onUserEdited}
          closeDialog={() => setEditAccountId(undefined)}
        />
      )}
      {deleteAccountId && (
        <InactivateAccountDialog
          accountId={deleteAccountId}
          closeDialog={() => setDeleteAccountId(undefined)}
          onUserInactivated={onUserInactivated}
          fullName={
            accounts?.find(e => e.id === deleteAccountId)?.fullName ?? ''
          }
        />
      )}
    </>
  );
};

const ActionsDropdown = (props: {
  onEditClick: () => void;
  onDeleteClick: () => void;
}) => {
  const { t: translate } = useTranslation();
  return (
    <>
      <Button className="btn" onClick={props.onEditClick}>
        {translate(resources.accounts.list.columns.actions.edit)}
      </Button>
      <Button className="btn" onClick={props.onDeleteClick}>
        {translate(resources.accounts.list.columns.actions.delete)}
      </Button>
    </>
  );
};
